import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@link/ui";
import { downloadFileBlob } from "../../lib/upload";

/** No public URLs or plaintext disk cache: previews share the download/decrypt path. */
export function AttachmentImage({ attachmentXid, accountXid, name }: {
  attachmentXid: string; accountXid: string; name: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    let started = false;
    setUrl(null);
    setFailed(false);
    setOpen(false);
    const load = () => {
      if (started) return;
      started = true;
      void downloadFileBlob(attachmentXid, accountXid, { signal: controller.signal, imagePreview: true })
        .then(({ blob }) => {
          if (controller.signal.aborted) return;
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    };
    const observer = typeof IntersectionObserver === "undefined" ? null : new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { load(); observer?.disconnect(); }
    }, { rootMargin: "200px" });
    if (observer && container.current) observer.observe(container.current);
    else load();
    return () => {
      controller.abort();
      observer?.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachmentXid, accountXid]);

  return <div ref={container} className="my-1 w-fit max-w-full">
    {failed ? <p className="py-2 text-xs text-cloud-500" role="status">Couldn't load the image preview. You can still download the file below.</p>
      : url === null ? <div className="flex h-40 w-64 max-w-full items-center justify-center rounded-xl bg-cloud-100 text-xs text-cloud-500" role="status">Loading image…</div>
      : <button type="button" onClick={() => setOpen(true)} aria-label={`View image: ${name}`} className="block max-w-full cursor-zoom-in overflow-hidden rounded-xl border border-cloud-200 focus-visible:ring-2 focus-visible:ring-strawberry-400">
          <img src={url} alt={name} onError={() => setFailed(true)} className="max-h-80 max-w-full object-contain" />
        </button>}
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[min(1100px,95vw)] overflow-hidden p-4">
        <DialogTitle className="break-all pr-8 text-sm">{name}</DialogTitle>
        <DialogDescription className="sr-only">Full image preview</DialogDescription>
        {url && <img src={url} alt={name} className="mx-auto max-h-[75dvh] max-w-full object-contain" />}
      </DialogContent>
    </Dialog>
  </div>;
}
